<template>
  <section class="profit-panel">
    <el-alert
      v-if="summary && !summary.enabled"
      title="分账总开关未开启，以下为历史数据"
      type="warning"
      show-icon
      :closable="false"
    />

    <div v-if="section === 'payments'" class="summary-grid">
      <article v-for="stat in summaryStats" :key="stat.label" class="summary-card">
        <span>{{ stat.label }}</span>
        <strong>{{ stat.value }}</strong>
        <small>{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <template v-if="section === 'payments'">
          <el-select v-model="paymentFilters.status" clearable placeholder="全部状态" class="filter-control">
            <el-option v-for="item in paymentStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-select v-model="paymentFilters.storeId" clearable filterable placeholder="全部门店" class="filter-control">
            <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="Number(store.id)" />
          </el-select>
          <el-button type="primary" @click="reloadPayments">查询</el-button>
          <el-button @click="resetPaymentFilters">重置</el-button>
        </template>
        <template v-else-if="section === 'rules'">
          <el-select v-model="ruleStoreId" clearable filterable placeholder="全局默认规则" class="filter-control">
            <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="Number(store.id)" />
          </el-select>
          <el-button type="primary" @click="loadRules">查询</el-button>
          <el-button v-if="canManage" type="primary" @click="openRuleEditor()">新增规则</el-button>
        </template>
        <template v-else>
          <el-button v-if="canManage" type="primary" @click="openReceiverEditor()">新增接收方</el-button>
          <span class="hint">新增接收方不传出资商户号、AppID，由后端读取当前启用配置。</span>
        </template>
      </div>

      <div v-if="section === 'payments'" v-loading="loadingPayments" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>订单号</th>
              <th>门店</th>
              <th class="is-right">支付金额</th>
              <th>状态</th>
              <th>规则版本</th>
              <th>更新时间</th>
              <th class="actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paymentPage?.list ?? []" :key="item.id">
              <td><button class="link-button" @click="openPaymentOrders(item)">{{ item.orderNo || item.orderId }}</button></td>
              <td>{{ getStoreName(item.storeId) }}</td>
              <td class="is-right">{{ formatAmount(item.paidAmount ?? 0) }}</td>
              <td><span :class="['status-tag', toneClass(paymentStatusTone(item.status))]">{{ getPaymentStatusText(item.status) }}</span></td>
              <td>{{ item.ruleVersion || '--' }}</td>
              <td>{{ item.updatedAt || '--' }}</td>
              <td class="actions">
                <el-button size="small" :disabled="!canExecute || !canExecutePayment(item.status)" :loading="actingId === `execute-${item.id}`" @click="confirmExecutePayment(item)">人工执行</el-button>
                <el-button size="small" :disabled="!canExecute || item.status !== SHARE_PAYMENT_STATUS.Shared" :loading="actingId === `unfreeze-${item.id}`" @click="confirmUnfreezePayment(item)">人工解冻</el-button>
              </td>
            </tr>
            <tr v-if="!(paymentPage?.list.length) && !loadingPayments"><td colspan="7"><el-empty description="暂无分账支付单" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'rules'" v-loading="loadingRules" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>名称</th>
              <th>范围</th>
              <th>版本</th>
              <th>状态</th>
              <th>明细</th>
              <th>生效时间</th>
              <th>备注</th>
              <th class="actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in sortedRules" :key="item.id">
              <td>{{ item.name }}</td>
              <td>{{ item.global ? '全局' : (item.storeName || getStoreName(item.storeId)) }}</td>
              <td>V{{ item.version }}</td>
              <td><span :class="['status-tag', toneClass(item.enabled ? 'success' : 'muted')]">{{ item.enabled ? '生效' : '已停用' }}</span></td>
              <td>{{ formatRuleItems(item.items) }}</td>
              <td>{{ item.effectiveFrom || '--' }}</td>
              <td>{{ item.remark || '--' }}</td>
              <td class="actions">
                <el-button size="small" :disabled="!canManage || !item.enabled" :loading="actingId === `disable-rule-${item.id}`" @click="confirmDisableRule(item)">停用</el-button>
              </td>
            </tr>
            <tr v-if="!sortedRules.length && !loadingRules"><td colspan="8"><el-empty description="暂无分账规则" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else v-loading="loadingReceivers" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>商户号</th>
              <th>主体全称</th>
              <th>关系类型</th>
              <th>状态</th>
              <th>允许回退</th>
              <th>更新时间</th>
              <th class="actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in receivers" :key="item.id">
              <td>{{ item.account }}</td>
              <td>{{ item.name || '--' }}</td>
              <td>{{ item.relationType === 'CUSTOM' ? item.customRelation : getRelationText(item.relationType) }}</td>
              <td><span :class="['status-tag', toneClass(receiverStatusTone(item.status))]">{{ getReceiverStatusText(item.status) }}</span></td>
              <td>{{ item.returnAllowed ? '是' : '否' }}</td>
              <td>{{ item.updatedAt || '--' }}</td>
              <td class="actions">
                <el-button size="small" :disabled="!canManage || item.status !== SHARE_RECEIVER_STATUS.Draft" :loading="actingId === `activate-${item.id}`" @click="confirmActivateReceiver(item)">激活</el-button>
                <el-button size="small" type="danger" plain :disabled="!canManage || item.status === SHARE_RECEIVER_STATUS.Removed" :loading="actingId === `remove-${item.id}`" @click="confirmRemoveReceiver(item)">移除</el-button>
              </td>
            </tr>
            <tr v-if="!receivers.length && !loadingReceivers"><td colspan="7"><el-empty description="暂无分账接收方" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="section === 'payments' && paymentPage" class="table-footer">
        <span>共 {{ paymentPage.total }} 条记录，第 {{ paymentPage.page }}/{{ paymentTotalPages }} 页</span>
        <div>
          <el-button :disabled="paymentPage.page <= 1" @click="changePaymentPage(paymentPage.page - 1)">上一页</el-button>
          <el-button :disabled="!paymentPage.hasMore" @click="changePaymentPage(paymentPage.page + 1)">下一页</el-button>
        </div>
      </div>
    </article>

    <article v-if="section === 'payments'" class="content-card">
      <div class="filter-bar">
        <el-select v-model="orderFilters.kind" clearable placeholder="全部类型" class="filter-control">
          <el-option label="分账" value="SHARE" />
          <el-option label="解冻" value="UNFREEZE" />
        </el-select>
        <el-select v-model="orderFilters.state" clearable placeholder="全部状态" class="filter-control">
          <el-option v-for="item in orderStateOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-button type="primary" @click="reloadOrders">查询分账单</el-button>
        <el-select v-model="returnFilters.result" clearable placeholder="全部回退结果" class="filter-control">
          <el-option v-for="item in returnResultOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-button @click="reloadReturns">查询回退单</el-button>
      </div>
      <el-tabs>
        <el-tab-pane label="分账单">
          <div v-loading="loadingOrders" class="table-wrap">
            <table>
              <thead><tr><th>单号</th><th>订单号</th><th>类型</th><th class="is-right">金额</th><th>状态</th><th>更新时间</th><th class="actions">操作</th></tr></thead>
              <tbody>
                <tr v-for="item in orderPage?.list ?? []" :key="item.id">
                  <td><button class="link-button" @click="openOrderDetail(item)">{{ item.outOrderNo || item.id }}</button></td>
                  <td>{{ item.orderNo || item.orderId }}</td>
                  <td>{{ item.kind === 'UNFREEZE' ? '解冻' : '分账' }}</td>
                  <td class="is-right">{{ formatAmount(item.amount ?? 0) }}</td>
                  <td><span :class="['status-tag', toneClass(orderStateTone(item.state))]">{{ getOrderStateText(item.state) }}</span></td>
                  <td>{{ item.updatedAt || '--' }}</td>
                  <td class="actions"><el-button size="small" :disabled="!canExecute || isOrderFinal(item.state)" :loading="actingId === `requery-order-${item.id}`" @click="confirmRequeryOrder(item)">立即重查</el-button></td>
                </tr>
                <tr v-if="!(orderPage?.list.length) && !loadingOrders"><td colspan="7"><el-empty description="暂无分账单" /></td></tr>
              </tbody>
            </table>
          </div>
        </el-tab-pane>
        <el-tab-pane label="回退单">
          <div v-loading="loadingReturns" class="table-wrap">
            <table>
              <thead><tr><th>回退单号</th><th>微信单号</th><th class="is-right">金额</th><th>结果</th><th>失败原因</th><th>更新时间</th><th class="actions">操作</th></tr></thead>
              <tbody>
                <tr v-for="item in returnPage?.list ?? []" :key="item.id">
                  <td>{{ item.outReturnNo || item.id }}</td>
                  <td>{{ item.wechatReturnId || '--' }}</td>
                  <td class="is-right">{{ formatAmount(item.amount ?? 0) }}</td>
                  <td><span :class="['status-tag', toneClass(returnResultTone(item.result))]">{{ getReturnResultText(item.result) }}</span></td>
                  <td>{{ item.failReason || '--' }}</td>
                  <td>{{ item.updatedAt || '--' }}</td>
                  <td class="actions"><el-button size="small" :disabled="!canExecute || isReturnFinal(item.result)" :loading="actingId === `requery-return-${item.id}`" @click="confirmRequeryReturn(item)">立即重查</el-button></td>
                </tr>
                <tr v-if="!(returnPage?.list.length) && !loadingReturns"><td colspan="7"><el-empty description="暂无回退单" /></td></tr>
              </tbody>
            </table>
          </div>
        </el-tab-pane>
      </el-tabs>
    </article>

    <el-dialog v-model="orderDetailVisible" title="分账明细" width="820px">
      <div v-loading="loadingOrderDetail" class="table-wrap">
        <table>
          <thead><tr><th>接收方商户号</th><th>名称</th><th class="is-right">金额</th><th>结果</th><th>失败原因</th></tr></thead>
          <tbody>
            <tr v-for="detail in selectedOrder?.details ?? []" :key="detail.id">
              <td>{{ detail.receiverAccount || '--' }}</td>
              <td>{{ detail.receiverName || '--' }}</td>
              <td class="is-right">{{ formatAmount(detail.amount ?? 0) }}</td>
              <td>{{ detail.result || '--' }}</td>
              <td>{{ detail.failReason || '--' }}</td>
            </tr>
            <tr v-if="!(selectedOrder?.details?.length)"><td colspan="5"><el-empty description="暂无接收方明细" /></td></tr>
          </tbody>
        </table>
      </div>
    </el-dialog>

    <el-dialog v-model="ruleEditorVisible" title="保存分账规则" width="900px">
      <el-form label-width="110px">
        <el-form-item label="所属门店">
          <el-select v-model="ruleForm.storeId" clearable filterable placeholder="不选则保存为全局规则">
            <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="Number(store.id)" />
          </el-select>
        </el-form-item>
        <el-form-item label="规则名称"><el-input v-model.trim="ruleForm.name" /></el-form-item>
        <el-form-item label="备注"><el-input v-model.trim="ruleForm.remark" type="textarea" :rows="2" /></el-form-item>
      </el-form>
      <div class="dialog-toolbar"><el-button type="primary" @click="appendRuleItem">新增明细</el-button></div>
      <div class="table-wrap">
        <table>
          <thead><tr><th>接收方</th><th>基数</th><th>比例万分比</th><th>固定金额</th><th>最小金额</th><th>最大金额</th><th>排序</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(item, index) in ruleForm.items" :key="index">
              <td><el-select v-model="item.receiverId" filterable placeholder="接收方"><el-option v-for="receiver in activeReceivers" :key="receiver.id" :label="`${receiver.name || receiver.account}（${receiver.account}）`" :value="receiver.id" /></el-select></td>
              <td><el-select v-model="item.baseType"><el-option label="实付额" value="PAID_AMOUNT" /><el-option label="商品额" value="GOODS_AMOUNT" /></el-select></td>
              <td><el-input-number v-model="item.ratioBp" :min="0" :max="10000" controls-position="right" /></td>
              <td><el-input-number v-model="item.fixedYuan" :min="0" :precision="2" controls-position="right" /></td>
              <td><el-input-number v-model="item.minYuan" :min="0" :precision="2" controls-position="right" /></td>
              <td><el-input-number v-model="item.maxYuan" :min="0" :precision="2" controls-position="right" /></td>
              <td><el-input-number v-model="item.sort" :min="0" controls-position="right" /></td>
              <td><el-button text type="danger" @click="ruleForm.items.splice(index, 1)">删除</el-button></td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #footer>
        <el-button @click="ruleEditorVisible = false">取消</el-button>
        <el-button type="primary" :loading="submittingRule" @click="submitRule">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="receiverEditorVisible" title="新增分账接收方" width="640px">
      <el-form label-width="120px">
        <el-form-item label="商户号" required><el-input v-model.trim="receiverForm.account" /></el-form-item>
        <el-form-item label="主体全称" required><el-input v-model.trim="receiverForm.name" /></el-form-item>
        <el-form-item label="关系类型" required>
          <el-select v-model="receiverForm.relationType">
            <el-option v-for="item in relationOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="receiverForm.relationType === 'CUSTOM'" label="自定义关系" required><el-input v-model.trim="receiverForm.customRelation" /></el-form-item>
        <el-form-item label="所属门店"><el-select v-model="receiverForm.storeId" clearable filterable><el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="Number(store.id)" /></el-select></el-form-item>
        <el-form-item label="允许退款回退"><el-switch v-model="receiverForm.returnAllowed" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="receiverEditorVisible = false">取消</el-button>
        <el-button type="primary" :loading="submittingReceiver" @click="submitReceiver">保存</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Permission, SHARE_ORDER_STATE, SHARE_PAYMENT_STATUS, SHARE_RECEIVER_STATUS, SHARE_RETURN_RESULT } from '@jiaofa/constants'
import type { AdminStore } from '@/api/store'
import { fetchStores } from '@/api/store'
import { useUserStore } from '@/stores/user'
import { formatAmount } from '@/views/operation/utils/operationFormatters'
import {
  activateShareReceiver,
  createShareReceiver,
  disableShareRule,
  executeSharePayment,
  fetchShareOrderDetail,
  fetchShareOrders,
  fetchSharePayments,
  fetchShareReceivers,
  fetchShareReturns,
  fetchShareRules,
  fetchShareSummary,
  removeShareReceiver,
  requeryShareOrder,
  requeryShareReturn,
  saveShareRule,
  unfreezeSharePayment,
  type ShareOrder,
  type SharePayment,
  type ShareReceiver,
  type ShareRule,
  type ShareSummary,
} from '@/api/profit-sharing'

type Section = 'payments' | 'rules' | 'receivers'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{ section: Section }>()
const userStore = useUserStore()
const canManage = computed(() => userStore.hasEveryPermission([Permission.ProfitSharingManage]))
const canExecute = computed(() => userStore.hasEveryPermission([Permission.ProfitSharingExecute]))

const summary = ref<ShareSummary>()
const paymentPage = ref<Awaited<ReturnType<typeof fetchSharePayments>>>()
const orderPage = ref<Awaited<ReturnType<typeof fetchShareOrders>>>()
const returnPage = ref<Awaited<ReturnType<typeof fetchShareReturns>>>()
const rules = ref<ShareRule[]>([])
const receivers = ref<ShareReceiver[]>([])
const storeOptions = ref<AdminStore[]>([])
const selectedOrder = ref<ShareOrder>()
const actingId = ref('')
const loadingPayments = ref(false)
const loadingOrders = ref(false)
const loadingReturns = ref(false)
const loadingRules = ref(false)
const loadingReceivers = ref(false)
const loadingOrderDetail = ref(false)
const orderDetailVisible = ref(false)
const ruleEditorVisible = ref(false)
const receiverEditorVisible = ref(false)
const submittingRule = ref(false)
const submittingReceiver = ref(false)
const ruleStoreId = ref<number>()

const paymentFilters = reactive({ status: '', storeId: undefined as number | undefined, page: 1, pageSize: 10 })
const orderFilters = reactive({ state: '', kind: '', page: 1, pageSize: 10 })
const returnFilters = reactive({ result: '', page: 1, pageSize: 10 })
const ruleForm = reactive({
  storeId: undefined as number | undefined,
  name: '',
  remark: '',
  items: [] as Array<{ receiverId?: number; baseType: 'PAID_AMOUNT' | 'GOODS_AMOUNT'; ratioBp?: number; fixedYuan?: number; minYuan?: number; maxYuan?: number; sort?: number }>,
})
const receiverForm = reactive({ storeId: undefined as number | undefined, account: '', name: '', relationType: 'STORE', customRelation: '', returnAllowed: true })

const paymentStatusOptions = [
  { value: SHARE_PAYMENT_STATUS.PendingPayment, label: '待支付' },
  { value: SHARE_PAYMENT_STATUS.Paid, label: '待分账' },
  { value: SHARE_PAYMENT_STATUS.Sharing, label: '分账中' },
  { value: SHARE_PAYMENT_STATUS.Shared, label: '待解冻' },
  { value: SHARE_PAYMENT_STATUS.Closed, label: '已完结' },
  { value: SHARE_PAYMENT_STATUS.Manual, label: '需人工' },
  { value: SHARE_PAYMENT_STATUS.Cancelled, label: '已作废' },
]
const orderStateOptions = [
  { value: SHARE_ORDER_STATE.Draft, label: '已落库' },
  { value: SHARE_ORDER_STATE.Processing, label: '处理中' },
  { value: SHARE_ORDER_STATE.Success, label: '成功' },
  { value: SHARE_ORDER_STATE.PartialFailed, label: '部分失败' },
  { value: SHARE_ORDER_STATE.Failed, label: '失败' },
  { value: SHARE_ORDER_STATE.Unknown, label: '待核实' },
]
const returnResultOptions = [
  { value: SHARE_RETURN_RESULT.Draft, label: '已落库' },
  { value: SHARE_RETURN_RESULT.Processing, label: '处理中' },
  { value: SHARE_RETURN_RESULT.Success, label: '已退回' },
  { value: SHARE_RETURN_RESULT.Failed, label: '失败' },
  { value: SHARE_RETURN_RESULT.Unknown, label: '待核实' },
]
const relationOptions = ['STORE', 'STAFF', 'STORE_OWNER', 'PARTNER', 'HEADQUARTER', 'BRAND', 'DISTRIBUTOR', 'USER', 'SUPPLIER', 'CUSTOM']
  .map((value) => ({ value, label: getRelationText(value) }))

const summaryStats = computed(() => [
  { label: '待分账', value: String(summary.value?.pendingCount ?? 0), sub: '已支付待处理' },
  { label: '分账中', value: String(summary.value?.sharingCount ?? 0), sub: '微信处理中' },
  { label: '待解冻', value: String(summary.value?.sharedCount ?? 0), sub: '分账已完成' },
  { label: '需人工', value: String(summary.value?.manualCount ?? 0), sub: '超期待处理' },
  { label: '已分金额', value: formatAmount(summary.value?.sharedAmountFen ?? 0), sub: '累计成功' },
  { label: '回退待处理', value: String(summary.value?.returnPendingCount ?? 0), sub: '退款闸门' },
  { label: '回退失败', value: String(summary.value?.returnFailedCount ?? 0), sub: '需重查或人工' },
])
const sortedRules = computed(() => [...rules.value].sort((a, b) => Number(b.storeId ?? 0) - Number(a.storeId ?? 0) || b.version - a.version))
const activeReceivers = computed(() => receivers.value.filter((item) => item.status === SHARE_RECEIVER_STATUS.Active))
const paymentTotalPages = computed(() => Math.max(1, Math.ceil((paymentPage.value?.total ?? 0) / (paymentPage.value?.pageSize || paymentFilters.pageSize))))

watch(() => props.section, () => refresh(), { immediate: true })

async function refresh(): Promise<void> {
  await ensureStores()
  if (props.section === 'payments') {
    await Promise.all([loadSummary(), loadPayments(), loadOrders(), loadReturns()])
  } else if (props.section === 'rules') {
    await Promise.all([loadReceivers(), loadRules()])
  } else {
    await loadReceivers()
  }
}

async function ensureStores(): Promise<void> {
  if (storeOptions.value.length) return
  try { storeOptions.value = await fetchStores({}) } catch { storeOptions.value = [] }
}

async function loadSummary(): Promise<void> {
  try { summary.value = await fetchShareSummary() } catch (error) { ElMessage.error(error instanceof Error ? error.message : '分账概览加载失败') }
}

async function loadPayments(): Promise<void> {
  loadingPayments.value = true
  try {
    paymentPage.value = await fetchSharePayments({
      status: paymentFilters.status || undefined,
      storeId: paymentFilters.storeId,
      page: paymentFilters.page,
      pageSize: paymentFilters.pageSize,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '分账支付单加载失败')
  } finally {
    loadingPayments.value = false
  }
}

async function loadOrders(): Promise<void> {
  loadingOrders.value = true
  try {
    orderPage.value = await fetchShareOrders({ state: orderFilters.state || undefined, kind: orderFilters.kind || undefined, page: orderFilters.page, pageSize: orderFilters.pageSize })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '分账单加载失败')
  } finally {
    loadingOrders.value = false
  }
}

async function loadReturns(): Promise<void> {
  loadingReturns.value = true
  try {
    returnPage.value = await fetchShareReturns({ result: returnFilters.result || undefined, page: returnFilters.page, pageSize: returnFilters.pageSize })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '回退单加载失败')
  } finally {
    loadingReturns.value = false
  }
}

async function loadRules(): Promise<void> {
  loadingRules.value = true
  try { rules.value = await fetchShareRules(ruleStoreId.value) } catch (error) { ElMessage.error(error instanceof Error ? error.message : '分账规则加载失败') } finally { loadingRules.value = false }
}

async function loadReceivers(): Promise<void> {
  loadingReceivers.value = true
  try { receivers.value = await fetchShareReceivers() } catch (error) { ElMessage.error(error instanceof Error ? error.message : '接收方加载失败') } finally { loadingReceivers.value = false }
}

function reloadPayments(): void {
  paymentFilters.page = 1
  void loadPayments()
}

function resetPaymentFilters(): void {
  paymentFilters.status = ''
  paymentFilters.storeId = undefined
  reloadPayments()
}

function reloadOrders(): void {
  orderFilters.page = 1
  void loadOrders()
}

function reloadReturns(): void {
  returnFilters.page = 1
  void loadReturns()
}

function changePaymentPage(page: number): void {
  paymentFilters.page = page
  void loadPayments()
}

async function openPaymentOrders(payment: SharePayment): Promise<void> {
  orderFilters.page = 1
  await loadOrders()
  orderPage.value = orderPage.value ? { ...orderPage.value, list: orderPage.value.list.filter((item) => item.paymentId === payment.id || item.paymentId === payment.paymentId) } : orderPage.value
}

async function openOrderDetail(order: ShareOrder): Promise<void> {
  orderDetailVisible.value = true
  loadingOrderDetail.value = true
  try { selectedOrder.value = await fetchShareOrderDetail(order.id) } catch (error) { ElMessage.error(error instanceof Error ? error.message : '分账明细加载失败') } finally { loadingOrderDetail.value = false }
}

async function withConfirm(message: string, actionKey: string, action: () => Promise<unknown>): Promise<void> {
  await ElMessageBox.confirm(message, '确认操作', { type: 'warning' })
  actingId.value = actionKey
  try {
    await action()
    ElMessage.success('操作成功')
    await refresh()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '操作失败')
  } finally {
    actingId.value = ''
  }
}

function confirmExecutePayment(item: SharePayment): Promise<void> {
  return withConfirm('确认对该支付单执行或补充分账？', `execute-${item.id}`, () => executeSharePayment(item.id))
}

function confirmUnfreezePayment(item: SharePayment): Promise<void> {
  return withConfirm('确认对该支付单人工解冻剩余资金？', `unfreeze-${item.id}`, () => unfreezeSharePayment(item.id))
}

function confirmRequeryOrder(item: ShareOrder): Promise<void> {
  return withConfirm('确认按原分账单号立即重查？', `requery-order-${item.id}`, () => requeryShareOrder(item.id))
}

function confirmRequeryReturn(item: { id: number }): Promise<void> {
  return withConfirm('确认按原回退单号立即重查？', `requery-return-${item.id}`, () => requeryShareReturn(item.id))
}

function confirmDisableRule(item: ShareRule): Promise<void> {
  return withConfirm('确认停用该规则版本？', `disable-rule-${item.id}`, () => disableShareRule(item.id))
}

function confirmActivateReceiver(item: ShareReceiver): Promise<void> {
  return withConfirm('确认把该接收方注册到微信并激活？', `activate-${item.id}`, () => activateShareReceiver(item.id))
}

function confirmRemoveReceiver(item: ShareReceiver): Promise<void> {
  return withConfirm('确认移除该接收方？', `remove-${item.id}`, () => removeShareReceiver(item.id))
}

function openRuleEditor(): void {
  ruleForm.storeId = ruleStoreId.value
  ruleForm.name = ''
  ruleForm.remark = ''
  ruleForm.items = []
  appendRuleItem()
  ruleEditorVisible.value = true
}

function appendRuleItem(): void {
  ruleForm.items.push({ receiverId: undefined, baseType: 'PAID_AMOUNT', minYuan: 0, sort: ruleForm.items.length + 1 })
}

async function submitRule(): Promise<void> {
  const error = validateRule()
  if (error) {
    ElMessage.warning(error)
    return
  }
  submittingRule.value = true
  try {
    await saveShareRule({
      storeId: ruleForm.storeId,
      name: ruleForm.name,
      remark: ruleForm.remark || undefined,
      items: ruleForm.items.map((item) => ({
        receiverId: item.receiverId as number,
        baseType: item.baseType,
        ratioBp: item.ratioBp || undefined,
        fixedFen: yuanToFen(item.fixedYuan),
        minFen: yuanToFen(item.minYuan) ?? 0,
        maxFen: yuanToFen(item.maxYuan),
        sort: item.sort,
      })),
    })
    ElMessage.success('规则已保存')
    ruleEditorVisible.value = false
    await loadRules()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '规则保存失败')
  } finally {
    submittingRule.value = false
  }
}

function validateRule(): string {
  if (!ruleForm.name.trim()) return '请输入规则名称'
  if (!ruleForm.items.length) return '至少添加一条明细'
  for (const item of ruleForm.items) {
    if (!item.receiverId) return '请选择接收方'
    const hasRatio = item.ratioBp !== undefined && item.ratioBp > 0
    const hasFixed = item.fixedYuan !== undefined && item.fixedYuan > 0
    if (hasRatio === hasFixed) return '比例与固定金额必须二选一'
    if (item.ratioBp !== undefined && (item.ratioBp < 0 || item.ratioBp > 10000)) return '比例万分比需在 0 到 10000 之间'
    if (item.maxYuan !== undefined && item.minYuan !== undefined && item.maxYuan < item.minYuan) return '最大金额不能小于最小金额'
  }
  return ''
}

function openReceiverEditor(): void {
  Object.assign(receiverForm, { storeId: undefined, account: '', name: '', relationType: 'STORE', customRelation: '', returnAllowed: true })
  receiverEditorVisible.value = true
}

async function submitReceiver(): Promise<void> {
  if (!receiverForm.account || !receiverForm.name) {
    ElMessage.warning('请输入商户号和主体全称')
    return
  }
  if (receiverForm.relationType === 'CUSTOM' && !receiverForm.customRelation) {
    ElMessage.warning('请输入自定义关系')
    return
  }
  submittingReceiver.value = true
  try {
    await createShareReceiver({
      storeId: receiverForm.storeId,
      account: receiverForm.account,
      name: receiverForm.name,
      relationType: receiverForm.relationType,
      customRelation: receiverForm.relationType === 'CUSTOM' ? receiverForm.customRelation : undefined,
      returnAllowed: receiverForm.returnAllowed,
    })
    ElMessage.success('接收方已保存')
    receiverEditorVisible.value = false
    await loadReceivers()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '接收方保存失败')
  } finally {
    submittingReceiver.value = false
  }
}

function yuanToFen(value?: number): number | undefined {
  return value === undefined ? undefined : Math.round(value * 100)
}

function getStoreName(storeId?: number): string {
  if (!storeId) return '全局'
  return storeOptions.value.find((store) => Number(store.id) === storeId)?.name ?? String(storeId)
}

function getPaymentStatusText(status: string): string {
  return paymentStatusOptions.find((item) => item.value === status)?.label ?? status
}

function getOrderStateText(state: string): string {
  return orderStateOptions.find((item) => item.value === state)?.label ?? state
}

function getReturnResultText(result: string): string {
  return returnResultOptions.find((item) => item.value === result)?.label ?? result
}

function getReceiverStatusText(status: string): string {
  return status === SHARE_RECEIVER_STATUS.Draft ? '未注册' : status === SHARE_RECEIVER_STATUS.Active ? '已生效' : status === SHARE_RECEIVER_STATUS.Removed ? '已移除' : '状态未知'
}

function getRelationText(value?: string): string {
  const text: Record<string, string> = {
    STORE: '门店',
    STAFF: '员工',
    STORE_OWNER: '店主',
    PARTNER: '合作伙伴',
    HEADQUARTER: '总部',
    BRAND: '品牌方',
    DISTRIBUTOR: '分销商',
    USER: '用户',
    SUPPLIER: '供应商',
    CUSTOM: '自定义',
  }
  return value ? (text[value] ?? value) : '--'
}

function formatRuleItems(items: ShareRule['items']): string {
  return items.map((item) => `${item.receiverName || item.receiverAccount || item.receiverId}: ${item.ratioBp ? `${item.ratioBp / 100}%` : formatAmount(item.fixedFen ?? 0)}`).join('；') || '--'
}

function canExecutePayment(status: string): boolean {
  return [SHARE_PAYMENT_STATUS.Paid, SHARE_PAYMENT_STATUS.Sharing, SHARE_PAYMENT_STATUS.Shared, SHARE_PAYMENT_STATUS.Manual].includes(status as never)
}

function isOrderFinal(state: string): boolean {
  return [SHARE_ORDER_STATE.Success, SHARE_ORDER_STATE.PartialFailed, SHARE_ORDER_STATE.Failed].includes(state as never)
}

function isReturnFinal(result: string): boolean {
  return [SHARE_RETURN_RESULT.Success, SHARE_RETURN_RESULT.Failed].includes(result as never)
}

function toneClass(tone: StatusTone): string {
  return `is-${tone}`
}

function paymentStatusTone(status: string): StatusTone {
  if (status === SHARE_PAYMENT_STATUS.Closed) return 'success'
  if (status === SHARE_PAYMENT_STATUS.Manual) return 'error'
  if (status === SHARE_PAYMENT_STATUS.Shared || status === SHARE_PAYMENT_STATUS.Sharing) return 'warning'
  if (status === SHARE_PAYMENT_STATUS.Cancelled) return 'muted'
  return 'brand'
}

function orderStateTone(state: string): StatusTone {
  if (state === SHARE_ORDER_STATE.Success) return 'success'
  if (state === SHARE_ORDER_STATE.Failed || state === SHARE_ORDER_STATE.PartialFailed) return 'error'
  if (state === SHARE_ORDER_STATE.Unknown || state === SHARE_ORDER_STATE.Processing) return 'warning'
  return 'brand'
}

function returnResultTone(result: string): StatusTone {
  if (result === SHARE_RETURN_RESULT.Success) return 'success'
  if (result === SHARE_RETURN_RESULT.Failed) return 'error'
  if (result === SHARE_RETURN_RESULT.Unknown || result === SHARE_RETURN_RESULT.Processing) return 'warning'
  return 'brand'
}

function receiverStatusTone(status: string): StatusTone {
  if (status === SHARE_RECEIVER_STATUS.Active) return 'success'
  if (status === SHARE_RECEIVER_STATUS.Removed) return 'muted'
  if (status === SHARE_RECEIVER_STATUS.Unknown) return 'warning'
  return 'brand'
}

defineExpose({ refresh, openRuleEditor, openReceiverEditor })
</script>

<style scoped lang="scss">
.profit-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 18px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { display: block; color: var(--jf-color-text-primary); font-size: 24px; line-height: 1.2; }
.summary-card small { color: var(--jf-color-text-secondary); font-size: 12px; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 64px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.filter-control { width: 180px; }
.hint { color: var(--jf-color-text-secondary); font-size: 13px; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.actions { min-width: 150px; text-align: right; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.link-button { border: 0; background: transparent; color: var(--jf-color-primary); cursor: pointer; font: inherit; padding: 0; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; color: var(--jf-color-text-secondary); font-size: 14px; }
.dialog-toolbar { margin-bottom: 12px; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
